// NEW_FILE_JUSTIFIED: Import employee hook — manages file upload, AI extraction, and save flow for ImportEmployeeDialog
import { useState, useCallback } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks need convex Id type
import type { IEmployeeDocument, IUploadedFile, IImportState } from '@/@types/company';

const initialState: IImportState = {
  step: 'upload',
  uploadedFiles: [],
  pendingFileNames: [],
  extractedData: null,
  error: null,
};

export function useImportEmployee(companyId: Id<'companies'> | null) {
  const [state, setState] = useState<IImportState>(initialState);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const extractEmployeeData = useAction(
    api['companies/extractEmployee'].extractEmployeeData
  );
  const createEmployee = useMutation(api.companies.employees.create);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      setState(prev => ({
        ...prev,
        step: 'extracting',
        pendingFileNames: files.map(f => f.name),
        error: null,
      }));

      const uploaded: IUploadedFile[] = [];

      try {
        for (const file of files) {
          const uploadUrl = await createUploadUrl({});
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          const { storageId } = (await response.json()) as {
            storageId: Id<'_storage'>;
          }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
          uploaded.push({ storageId, fileName: file.name });
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setState(prev => ({ ...prev, step: 'error', error: message }));
        return;
      }

      setState(prev => ({ ...prev, uploadedFiles: uploaded }));

      if (!companyId) {
        setState(prev => ({ ...prev, step: 'error', error: 'No company selected' }));
        return;
      }

      try {
        const result = await extractEmployeeData({
          companyId,
          storageIds: uploaded.map(f => f.storageId),
        });
        if (result.success && result.data) {
          setState(prev => ({
            ...prev,
            extractedData: result.data,
            step: 'review',
            error: null,
          }));
        } else {
          setState(prev => ({
            ...prev,
            step: 'error',
            error: result.error ?? 'Extraction failed',
            uploadedFiles: [],
          }));
        }
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Unknown error during extraction';
        setState(prev => ({ ...prev, step: 'error', error: message, uploadedFiles: [] }));
      }
    },
    [companyId, createUploadUrl, extractEmployeeData]
  );

  const saveEmployee = useCallback(
    async (formData: {
      name: string;
      dateOfBirth?: string;
      identityDocumentNumber?: string;
      nationality?: string;
      city?: string;
      postalCode?: string;
      phoneNumber?: string;
      email?: string;
      documentAssignments: Record<
        'identityDocFront' | 'identityDocBack' | 'drivingLicenseFront' | 'drivingLicenseBack',
        IEmployeeDocument | undefined
      >;
    }) => {
      if (!companyId) return null;

      setState(prev => ({ ...prev, step: 'saving' }));

      try {
        const employeeId = await createEmployee({
          companyId,
          name: formData.name,
          dateOfBirth: formData.dateOfBirth || undefined,
          identityDocumentNumber: formData.identityDocumentNumber || undefined,
          nationality: formData.nationality || undefined,
          city: formData.city || undefined,
          postalCode: formData.postalCode || undefined,
          phoneNumber: formData.phoneNumber || undefined,
          email: formData.email || undefined,
          identityDocFront: formData.documentAssignments.identityDocFront,
          identityDocBack: formData.documentAssignments.identityDocBack,
          drivingLicenseFront: formData.documentAssignments.drivingLicenseFront,
          drivingLicenseBack: formData.documentAssignments.drivingLicenseBack,
        });

        setState(prev => ({ ...prev, step: 'done' }));
        return employeeId;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Save failed';
        setState(prev => ({ ...prev, step: 'error', error: message }));
        return null;
      }
    },
    [companyId, createEmployee]
  );

  return {
    ...state,
    uploadFiles,
    saveEmployee,
    reset,
  };
}
